/**
 * PermisLevageForm.jsx — Formulaire fidèle au template "Permis de Levage" Lafarge.
 * Reproduit les blocs du template :
 *   - En-tête (interne/sous-traité, date, société, nature, dates)
 *   - Moyen de levage (5 cases)
 *   - Vérifications matériel + portée max
 *   - Charge (masse, longueur, volume, densité, masse calculée)
 *   - LEVAGE COMPLEXE — Élingages (CMU min, CMU à utiliser, BigBag, vérifications)
 *   - Adéquation matériel (hauteur, charge totale)
 *   - Environnement / Zone d'exclusion / Météo (cônes, communication, vent)
 *   - Procédure 3 phases
 *   - Signataires (élingueur, chef de manœuvre, chargé de travaux, donneur d'ordre)
 */
import { ArrowUp } from 'lucide-react';
import FormationPicker from '@/components/FormationPicker';

function Checkbox({ checked, onChange, label, testid, className = '' }) {
  return (
    <label className={`flex items-start gap-2 cursor-pointer text-xs ${className}`} data-testid={testid}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-[#DA291C] flex-shrink-0"
      />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

function NumInput({ value, onChange, testid, suffix, placeholder }) {
  return (
    <div className="inline-flex items-center gap-1">
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        className="w-24 px-2 py-1 text-xs border border-stone-300 focus:border-[#09090B] outline-none font-mono"
      />
      {suffix && <span className="text-[10px] text-stone-500 uppercase">{suffix}</span>}
    </div>
  );
}

function SectionTitle({ children, accent = 'bg-[#09090B]' }) {
  return (
    <div className={`${accent} text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2`}>
      {children}
    </div>
  );
}

export default function PermisLevageForm({ extra, setExtra, dateDebut, dateFin, titre, entreprise, compact = false, siteCode = '' }) {
  const isSoustraite = !!entreprise && entreprise !== 'Travaux internes';

  return (
    <div className="space-y-4 border-2 border-orange-600 bg-white" data-testid="permis-levage-form">
      <div className="bg-orange-600 text-white px-4 py-3 flex items-center gap-3">
        <ArrowUp className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">PERMIS DE LEVAGE DE CHARGE</div>
          <div className="text-[10px] uppercase tracking-wider opacity-90">
            Pour toute opération de levage avec engin, élingage et zone d'exclusion.
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        {/* En-tête — masqué en mode compact (enfant d'AT, hérités du parent) */}
        {!compact && (
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Travail</div>
            <div className="flex gap-3 items-center">
              <span className={`px-2 py-1 border text-[11px] font-bold ${!isSoustraite ? 'bg-[#09090B] text-white border-[#09090B]' : 'bg-white border-stone-300'}`}>Interne</span>
              <span className={`px-2 py-1 border text-[11px] font-bold ${isSoustraite ? 'bg-[#09090B] text-white border-[#09090B]' : 'bg-white border-stone-300'}`}>Sous-traité</span>
              <span className="text-[10px] text-stone-500 italic">(auto)</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Société (si sous-traité)</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{entreprise || '—'}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Nature de l'opération</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{titre || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Début travaux</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{dateDebut || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Fin travaux</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{dateFin || '—'}</div>
          </div>
        </div>
        )}

        {/* Moyen de levage */}
        <div>
          <SectionTitle>Moyen de levage prévu</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid grid-cols-2 sm:grid-cols-5 gap-2 bg-orange-50/30">
            <Checkbox checked={extra.moy_grue} onChange={(v) => setExtra('moy_grue', v)} label="Grue" testid="pl-moy-grue" />
            <Checkbox checked={extra.moy_chariot_telescopique} onChange={(v) => setExtra('moy_chariot_telescopique', v)} label="Chariot télescopique" testid="pl-moy-chariot-tele" />
            <Checkbox checked={extra.moy_chariot_elevateur} onChange={(v) => setExtra('moy_chariot_elevateur', v)} label="Chariot élévateur" testid="pl-moy-chariot-elev" />
            <Checkbox checked={extra.moy_pont_palan} onChange={(v) => setExtra('moy_pont_palan', v)} label="Pont roulant / Palan" testid="pl-moy-pont" />
            <div className="flex items-center gap-2">
              <Checkbox checked={extra.moy_autre} onChange={(v) => setExtra('moy_autre', v)} label="Autre :" testid="pl-moy-autre" />
              <input
                type="text"
                value={extra.moy_autre_precision || ''}
                onChange={(e) => setExtra('moy_autre_precision', e.target.value)}
                data-testid="pl-moy-autre-precision"
                className="flex-1 px-2 py-0.5 text-xs border border-stone-300"
                placeholder="Préciser"
              />
            </div>
          </div>
        </div>

        {/* Vérifications matériel */}
        <div>
          <SectionTitle>Vérifications du matériel</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid grid-cols-2 gap-2 bg-stone-50/50">
            <Checkbox checked={extra.manuel_dispo} onChange={(v) => setExtra('manuel_dispo', v)} label="Manuel d'utilisation du matériel disponible sur site" testid="pl-manuel" />
            <Checkbox checked={extra.conforme_inspecte} onChange={(v) => setExtra('conforme_inspecte', v)} label="Conforme, en état et inspecté (moufle, crochet, linguet…)" testid="pl-conforme" />
            <Checkbox checked={extra.controleur_charge} onChange={(v) => setExtra('controleur_charge', v)} label="Contrôleur d'état de charge présent (obligatoire)" testid="pl-controleur" />
            <Checkbox checked={extra.indicateur_devers} onChange={(v) => setExtra('indicateur_devers', v)} label="Indicateur de dévers présent (obligatoire)" testid="pl-devers" />
            <div className="flex items-center gap-2 text-xs">
              <span>Portée maximale (cf. abaque) :</span>
              <NumInput value={extra.portee_max} onChange={(v) => setExtra('portee_max', v)} testid="pl-portee-max" suffix="m" />
            </div>
            <Checkbox checked={extra.tablier_levage} onChange={(v) => setExtra('tablier_levage', v)} label="Utilisation d'un tablier de levage" testid="pl-tablier" />
          </div>
        </div>

        {/* Charge */}
        <div>
          <SectionTitle>Caractéristiques de la charge</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 bg-amber-50/30">
            <div className="flex items-center gap-3 text-xs">
              <span className="flex-1">Masse de charge connue (pesée dynamomètre) :</span>
              <NumInput value={extra.masse_charge_tonnes} onChange={(v) => setExtra('masse_charge_tonnes', v)} testid="pl-masse" suffix="tonnes" />
            </div>
            <div className="text-[10px] italic text-stone-500">Sinon évaluer : Densité acier=8 / calcaire ou béton=2.5</div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex-1">Longueur du plus grand côté :</span>
              <NumInput value={extra.longueur_max} onChange={(v) => setExtra('longueur_max', v)} testid="pl-longueur" suffix="m" />
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>Volume matière : <NumInput value={extra.volume_matiere} onChange={(v) => setExtra('volume_matiere', v)} testid="pl-volume" suffix="m³" /></div>
              <div>Densité : <NumInput value={extra.densite} onChange={(v) => setExtra('densite', v)} testid="pl-densite" /></div>
              <div>Masse calculée : <NumInput value={extra.masse_calculee} onChange={(v) => setExtra('masse_calculee', v)} testid="pl-masse-calc" suffix="t" /></div>
            </div>
          </div>
        </div>

        {/* Élingages — LEVAGE COMPLEXE */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Levage complexe — Élingages autorisés</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 bg-red-50/30">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>CMU minimum d'élingue (yc coef de sécurité) : <NumInput value={extra.cmu_minimum} onChange={(v) => setExtra('cmu_minimum', v)} testid="pl-cmu-min" suffix="kg" /></div>
              <div>CMU d'élingage À UTILISER : <NumInput value={extra.cmu_utiliser} onChange={(v) => setExtra('cmu_utiliser', v)} testid="pl-cmu-util" suffix="kg" /></div>
            </div>
            <Checkbox checked={extra.elingage_bigbag} onChange={(v) => setExtra('elingage_bigbag', v)} label="Élingage en BigBag" testid="pl-bigbag" />
            <Checkbox checked={extra.elingues_compatibles} onChange={(v) => setExtra('elingues_compatibles', v)} label="Élingues compatibles à température / chimique" testid="pl-comp" />
            <Checkbox checked={extra.amarrage_present} onChange={(v) => setExtra('amarrage_present', v)} label="Présence et bon état des points d'amarrage de la pièce" testid="pl-amarrage" />
            <Checkbox checked={extra.elingage_inspecte} onChange={(v) => setExtra('elingage_inspecte', v)} label="Élingage et apparaux identifiés, en bon état, inspectés" testid="pl-inspecte" />
            <input
              type="text"
              value={extra.elingage_etat_degrade_precision || ''}
              onChange={(e) => setExtra('elingage_etat_degrade_precision', e.target.value)}
              data-testid="pl-etat-degrade"
              className="w-full px-2 py-1 text-xs border border-stone-300"
              placeholder="Si absents ou dégradés, moyen mis en œuvre…"
            />
            <Checkbox checked={extra.protection_aretes} onChange={(v) => setExtra('protection_aretes', v)} label="Protection des arêtes vives de la pièce" testid="pl-aretes" />
            <Checkbox checked={extra.charge_concentree} onChange={(v) => setExtra('charge_concentree', v)} label="Charge concentrée sur un volume minimal" testid="pl-concentre" />
            <Checkbox checked={extra.centre_gravite_estime} onChange={(v) => setExtra('centre_gravite_estime', v)} label="Position du centre de gravité estimée pour l'amarrage" testid="pl-cdg" />
          </div>
        </div>

        {/* Adéquation matériel */}
        <div>
          <SectionTitle>Adéquation matériel de levage / Chantier</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid grid-cols-2 gap-3 text-xs bg-stone-50/50">
            <div>Hauteur maxi du levage : <NumInput value={extra.hauteur_max_levage} onChange={(v) => setExtra('hauteur_max_levage', v)} testid="pl-hmax" suffix="m" /></div>
            <div>Charge totale (charge + élingage + moufle) : <NumInput value={extra.charge_totale} onChange={(v) => setExtra('charge_totale', v)} testid="pl-ch-tot" suffix="t" /></div>
          </div>
        </div>

        {/* Environnement / Météo */}
        <div>
          <SectionTitle>Environnement / Zone d'exclusion / Météo</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 bg-blue-50/30">
            <Checkbox checked={extra.absence_obstacles} onChange={(v) => setExtra('absence_obstacles', v)} label="Absence d'obstacles aériens ou d'ouvrages enterrés" testid="pl-obstacles" />
            <Checkbox checked={extra.resistance_sol} onChange={(v) => setExtra('resistance_sol', v)} label="Avec engin : Résistance et planéité du sol + calage stabilisateurs" testid="pl-sol" />
            <Checkbox checked={extra.reperage_chemin} onChange={(v) => setExtra('reperage_chemin', v)} label="Repérage du chemin de levage (le plus court et bas possible)" testid="pl-chemin" />
            <Checkbox checked={extra.balisage_exclusion} onChange={(v) => setExtra('balisage_exclusion', v)} label="Balisage en dur de la zone d'exclusion (obligatoire)" testid="pl-balisage" />
            <div className="grid grid-cols-3 gap-2 text-xs items-end">
              <div>Lmax charge : <NumInput value={extra.lmax_charge} onChange={(v) => setExtra('lmax_charge', v)} testid="pl-lmax" suffix="m" /></div>
              <div>Hmax levage : <NumInput value={extra.hmax_levage} onChange={(v) => setExtra('hmax_levage', v)} testid="pl-hmax2" suffix="m" /></div>
              <div className="font-bold">Diamètre d'exclusion : <NumInput value={extra.diametre_exclusion} onChange={(v) => setExtra('diametre_exclusion', v)} testid="pl-diam" suffix="m" /></div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span>Type de communication :</span>
              <Checkbox checked={extra.comm_visuelle} onChange={(v) => setExtra('comm_visuelle', v)} label="Visuelle" testid="pl-comm-vis" />
              <Checkbox checked={extra.comm_radio} onChange={(v) => setExtra('comm_radio', v)} label="Radio" testid="pl-comm-radio" />
              <Checkbox checked={extra.comm_telephone} onChange={(v) => setExtra('comm_telephone', v)} label="Tél. portable (déconseillé)" testid="pl-comm-tel" />
            </div>
            <Checkbox checked={extra.absence_orage} onChange={(v) => setExtra('absence_orage', v)} label="Absence de risque d'orage" testid="pl-orage" />
            <Checkbox checked={extra.vent_max} onChange={(v) => setExtra('vent_max', v)} label="Vent < 40 km/h (cf. anémomètre / station météo / manche)" testid="pl-vent" />
            <Checkbox checked={extra.meteo_validation} onChange={(v) => setExtra('meteo_validation', v)} label="Météo à risque, maintien de l'opération validé par donneur d'ordre" testid="pl-meteo-val" />
            <Checkbox checked={extra.levage_3_phases} onChange={(v) => setExtra('levage_3_phases', v)} label="Levage en 3 phases : tension élingues → 5 cm → levage complet" testid="pl-3phases" />
          </div>
        </div>

        {/* Signataires */}
        <div>
          <SectionTitle>Signataires & Validations</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Élingueur</div>
              <input type="text" value={extra.elingueur || ''} onChange={(e) => setExtra('elingueur', e.target.value)} data-testid="pl-elingueur"
                     className="w-full px-2 py-1.5 border border-stone-300" placeholder="Nom + Prénom" />
              <div className="text-[10px] text-stone-500 italic mt-1">Responsable de la mise en œuvre de l'élingage défini</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Chef de manœuvre</div>
              <input type="text" value={extra.chef_manoeuvre || ''} onChange={(e) => setExtra('chef_manoeuvre', e.target.value)} data-testid="pl-chef"
                     className="w-full px-2 py-1.5 border border-stone-300" placeholder="Nom + Prénom" />
              <div className="text-[10px] text-stone-500 italic mt-1">Responsable de la manœuvre de levage de A à Z. Devoir d'arrêt en cas de danger.</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Chargé de travaux</div>
              <input type="text" value={extra.charge_travaux || ''} onChange={(e) => setExtra('charge_travaux', e.target.value)} data-testid="pl-charge-travaux"
                     className="w-full px-2 py-1.5 border border-stone-300" placeholder="Nom, fonction" />
            </div>
            <div>
              <FormationPicker
                module="permis_levage"
                site={siteCode}
                value={extra.donneur_ordre || ''}
                onChange={(name) => setExtra('donneur_ordre', name)}
                testid="pl-donneur"
                placeholder="Donneur d'ordre Lafarge (validateur PL)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
